import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Button, Tag, Spin, Alert, message, Divider } from 'antd';
import {
  UserOutlined,
  DollarOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  SearchOutlined,
  WalletOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  UpOutlined,
  CloseOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { assignReversalPoolToUser, searchUsersForAssignment } from '../../api/login/Login';

// Helper for distinct role styling
const getRoleMeta = (roleName = '') => {
  const r = (roleName || '').toLowerCase();
  if (r.includes('super distributor')) {
    return { icon: '👑', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', tagColor: 'purple' };
  }
  if (r.includes('distributor')) {
    return { icon: '👔', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', tagColor: 'green' };
  }
  if (r.includes('retailer')) {
    return { icon: '🏪', color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4', tagColor: 'cyan' };
  }
  if (r.includes('reseller partner')) {
    return { icon: '⚡', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', tagColor: 'orange' };
  }
  if (r.includes('reseller')) {
    return { icon: '🤝', color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8', tagColor: 'magenta' };
  }
  if (r.includes('api user')) {
    return { icon: '🔌', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', tagColor: 'blue' };
  }
  if (r.includes('asm')) {
    return { icon: '🛡️', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', tagColor: 'geekblue' };
  }
  if (r.includes('zsm')) {
    return { icon: '🌐', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe', tagColor: 'indigo' };
  }
  return { icon: '👤', color: '#475569', bg: '#f8fafc', border: '#cbd5e1', tagColor: 'default' };
};

function ReversalAssignModal({
  open,
  onClose,
  poolType,
  poolTitle,
  availableBalance,
  gradient,
  icon,
  badgeBg,
  badgeColor,
  onSuccess,
}) {
  // Member selection state
  const [allUsers, setAllUsers] = useState([]);
  const [groupedRoles, setGroupedRoles] = useState([]);
  const [activeHoverRole, setActiveHoverRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form transfer state
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [tpin, setTpin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const poolBal = Number(availableBalance || 0);

  // Fetch all users and role groups
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await searchUsersForAssignment();
      const payload = res?.data || res || {};
      const users = Array.isArray(payload.users) ? payload.users : [];
      const groups = Array.isArray(payload.grouped) ? payload.grouped : [];

      setAllUsers(users);
      setGroupedRoles(groups);

      if (groups.length > 0) {
        // Default to Super Distributor if present, else first role
        const defaultRole =
          groups.find((g) => g.roleName?.toLowerCase().includes('super distributor')) || groups[0];
        setActiveHoverRole(defaultRole);
      }
    } catch (err) {
      console.error('Failed to search users:', err);
      message.error('Failed to load members for assignment');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (open) {
      setAmount('');
      setRemark(`Assigned from ${poolTitle || 'Reversal Pool'} by Admin`);
      setTpin('');
      setSelectedUser(null);
      setSearchQuery('');
      setSelectorOpen(true);
      fetchUsers();
    }
  }, [open, poolTitle]);

  // Compute relevant wallet balance of selected user
  const getUserWalletBal = (user) => {
    if (!user) return 0;
    if (poolType === 'Main Wallet') return Number(user.main_wallet || 0);
    if (poolType === 'Aeps Wallet') return Number(user.aeps_wallet || 0);
    if (poolType === 'Cashback Wallet') return Number(user.cashback_wallet || 0);
    return Number(user.main_wallet || 0);
  };

  const userCurrentBal = getUserWalletBal(selectedUser);
  const numAmount = Number(amount || 0);
  const poolNewBal = poolBal - numAmount;
  const userNewBal = userCurrentBal + numAmount;

  const isAmountValid = numAmount > 0 && numAmount <= poolBal;
  const isFormReady = selectedUser && isAmountValid && tpin.trim().length > 0;

  // Real-time search across all members regardless of role
  const matchingSearchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allUsers.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const mobile = (u.mobile || '').toLowerCase();
      const refer = (u.refer_id || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const role = (u.user_type || '').toLowerCase();
      return (
        name.includes(q) ||
        mobile.includes(q) ||
        refer.includes(q) ||
        email.includes(q) ||
        role.includes(q)
      );
    });
  }, [allUsers, searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSelectorOpen(false);
    setSearchQuery('');
  };

  const handleQuickAmount = (val) => {
    if (val > poolBal) {
      setAmount(poolBal);
    } else {
      setAmount(val);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      message.error('Please select a recipient user');
      return;
    }
    if (!isAmountValid) {
      message.error(
        `Amount must be between ₹1 and ₹${poolBal.toLocaleString('en-IN', {
          minimumFractionDigits: 2,
        })}`
      );
      return;
    }
    if (!tpin.trim()) {
      message.error('Please enter your Admin TPIN');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userId: selectedUser._id,
        poolType: poolType || 'Main Wallet',
        amount: numAmount,
        remark: remark.trim() || `Fund assigned from ${poolTitle}`,
        tpin: tpin.trim(),
      };

      const res = await assignReversalPoolToUser(payload);
      const isSuccess =
        res?.data?.status_Code === 200 ||
        res?.data?.statusCode === 200 ||
        res?.status === 200 ||
        res?.statusCode === 200;

      if (isSuccess) {
        message.success(
          res?.data?.message ||
            res?.message ||
            `Successfully assigned ₹${numAmount.toLocaleString('en-IN', {
              minimumFractionDigits: 2,
            })} to ${selectedUser.name}!`
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        message.error(res?.data?.message || res?.message || 'Failed to assign reversal pool funds');
      }
    } catch (err) {
      console.error('Assignment error:', err);
      const errMsg =
        err?.response?.data?.message || err?.message || 'Failed to assign reversal pool funds';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRoleMeta = selectedUser ? getRoleMeta(selectedUser.user_type) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={780}
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!isFormReady}
          style={{
            background: isFormReady ? '#10b981' : undefined,
            borderColor: isFormReady ? '#059669' : undefined,
            fontWeight: 700,
          }}
        >
          Confirm & Transfer ₹{numAmount > 0 ? numAmount.toLocaleString('en-IN') : '0'}
        </Button>,
      ]}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 20 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: gradient || 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '1.25rem',
              boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
            }}
          >
            {icon || <WalletOutlined />}
          </div>
          <div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Assign Reversal Pool Funds
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
              Transfer collected reversal balance to any Member (Super Distributor / Distributor / Retailer / etc.)
            </div>
          </div>
        </div>
      }
    >
      {/* Source Pool Summary Card */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 12,
          padding: '14px 18px',
          border: '1px solid #e2e8f0',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 12,
              background: badgeBg || '#d1fae5',
              color: badgeColor || '#065f46',
              textTransform: 'uppercase',
            }}
          >
            Source Pool
          </span>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginTop: 4 }}>
            {poolTitle}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
            Available Reversal Balance
          </div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#059669', lineHeight: 1.2 }}>
            ₹{poolBal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Step 1: Cascading Recipient User Selection */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ fontWeight: 700, color: '#334155', fontSize: '0.88rem' }}>
            1. Select Recipient User (All Roles Supported) <span style={{ color: '#ef4444' }}>*</span>
          </label>
          {selectedUser && (
            <Button
              type="link"
              size="small"
              onClick={() => setSelectorOpen(!selectorOpen)}
              style={{ padding: 0, fontWeight: 600, fontSize: '0.8rem' }}
            >
              {selectorOpen ? 'Collapse Browser' : 'Change Recipient'}
            </Button>
          )}
        </div>

        {/* Selected User Highlight Card (when a user is chosen) */}
        {selectedUser && !selectorOpen && (
          <div
            style={{
              border: '2px solid #10b981',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
              borderRadius: 12,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.08)',
              cursor: 'pointer',
            }}
            onClick={() => setSelectorOpen(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: selectedRoleMeta?.bg || '#ecfdf5',
                  border: `2px solid ${selectedRoleMeta?.border || '#a7f3d0'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.3rem',
                }}
              >
                {selectedRoleMeta?.icon || '👤'}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a' }}>
                    {selectedUser.name}
                  </span>
                  <Tag
                    color={selectedRoleMeta?.tagColor || 'blue'}
                    style={{ fontSize: '0.72rem', margin: 0, fontWeight: 700 }}
                  >
                    {selectedUser.user_type}
                  </Tag>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 2 }}>
                  Code: <strong style={{ color: '#0284c7' }}>{selectedUser.refer_id || 'N/A'}</strong> | Mobile: <strong>{selectedUser.mobile}</strong>
                  {selectedUser.email ? ` | ${selectedUser.email}` : ''}
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                  Current {poolType}
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#059669' }}>
                  ₹{userCurrentBal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectorOpen(true);
                }}
                style={{ borderRadius: 6, fontWeight: 600 }}
              >
                Change
              </Button>
            </div>
          </div>
        )}

        {/* Cascading Role Browser Panel (open by default or when changing) */}
        {selectorOpen && (
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: 12,
              padding: 14,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          >
            {/* Real-time Cross-Role Search Input */}
            <div style={{ marginBottom: 12 }}>
              <Input
                prefix={<SearchOutlined style={{ color: '#0284c7', fontSize: '1.1rem' }} />}
                placeholder="Search any member by Name (e.g. Saurav), Mobile, or Refer Code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                size="large"
                style={{
                  borderRadius: 8,
                  borderColor: searchQuery ? '#3b82f6' : '#cbd5e1',
                  boxShadow: searchQuery ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : undefined,
                }}
              />
            </div>

            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                <Spin size="default" />
                <div style={{ marginTop: 8, fontSize: '0.85rem' }}>Loading all members and roles...</div>
              </div>
            ) : searchQuery.trim().length > 0 ? (
              /* VIEW A: Search Results across ALL roles */
              <div>
                <div
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: '#475569',
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    🔍 Search Results for "<strong>{searchQuery}</strong>"
                  </span>
                  <span style={{ color: '#0284c7' }}>
                    {matchingSearchResults.length} member{matchingSearchResults.length !== 1 ? 's' : ''} found
                  </span>
                </div>

                <div style={{ maxHeight: 270, overflowY: 'auto', paddingRight: 4 }}>
                  {matchingSearchResults.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
                      <ExclamationCircleOutlined style={{ fontSize: '1.5rem', marginBottom: 6 }} />
                      <div>No members found matching "{searchQuery}"</div>
                      <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                        Try searching by partial name, mobile number, or refer code.
                      </div>
                    </div>
                  ) : (
                    matchingSearchResults.map((u) => {
                      const meta = getRoleMeta(u.user_type);
                      const isSelected = selectedUser?._id === u._id;
                      const bal = getUserWalletBal(u);
                      return (
                        <div
                          key={u._id}
                          onClick={() => handleSelectUser(u)}
                          style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                            background: isSelected ? '#f0fdf4' : '#ffffff',
                            marginBottom: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#3b82f6';
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background: meta.bg,
                                border: `1px solid ${meta.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.1rem',
                                flexShrink: 0,
                              }}
                            >
                              {meta.icon}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a' }}>
                                  {u.name}
                                </span>
                                <Tag
                                  color={meta.tagColor}
                                  style={{ fontSize: '0.68rem', margin: 0, padding: '0 5px' }}
                                >
                                  {u.user_type}
                                </Tag>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                                Code: <strong style={{ color: '#0284c7' }}>{u.refer_id || 'N/A'}</strong> | Mobile: {u.mobile}
                              </div>
                            </div>
                          </div>

                          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                              {poolType}
                            </div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#059669' }}>
                              ₹{bal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              /* VIEW B: Cascading Role Selector (Left = Roles, Right = Members of Hovered Role) */
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#64748b',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Select Member Role (Hover to reveal members)</span>
                  <span>Total Members: {allUsers.length}</span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: '#f8fafc',
                    minHeight: 270,
                  }}
                >
                  {/* Left Column: Role Categories */}
                  <div
                    style={{
                      width: '42%',
                      borderRight: '1px solid #e2e8f0',
                      background: '#ffffff',
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {groupedRoles.map((role) => {
                      const meta = getRoleMeta(role.roleName);
                      const isActive = activeHoverRole?.roleId === role.roleId;
                      return (
                        <div
                          key={role.roleId}
                          onMouseEnter={() => setActiveHoverRole(role)}
                          onClick={() => setActiveHoverRole(role)}
                          style={{
                            padding: '10px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            background: isActive ? meta.bg : '#ffffff',
                            borderLeft: isActive ? `4px solid ${meta.color}` : '4px solid transparent',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'all 0.12s ease',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <span style={{ fontSize: '1.1rem' }}>{meta.icon}</span>
                            <span
                              style={{
                                fontSize: '0.85rem',
                                fontWeight: isActive ? 700 : 600,
                                color: isActive ? meta.color : '#334155',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {role.roleName}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Tag
                              color={isActive ? meta.tagColor : 'default'}
                              style={{
                                fontSize: '0.68rem',
                                margin: 0,
                                borderRadius: 10,
                                fontWeight: 700,
                              }}
                            >
                              {role.count}
                            </Tag>
                            <span style={{ color: isActive ? meta.color : '#cbd5e1', fontSize: '0.75rem' }}>
                              ›
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right Column: Members List of Active/Hovered Role */}
                  <div
                    style={{
                      width: '58%',
                      background: '#f8fafc',
                      padding: 10,
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {activeHoverRole ? (
                      <div>
                        <div
                          style={{
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            color: '#334155',
                            marginBottom: 8,
                            paddingBottom: 4,
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span>
                            {getRoleMeta(activeHoverRole.roleName).icon} {activeHoverRole.roleName} Members
                          </span>
                          <span style={{ color: '#64748b' }}>
                            ({activeHoverRole.members?.length || 0})
                          </span>
                        </div>

                        {(!activeHoverRole.members || activeHoverRole.members.length === 0) ? (
                          <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
                            No members in this role
                          </div>
                        ) : (
                          activeHoverRole.members.map((m) => {
                            const isSelected = selectedUser?._id === m._id;
                            const bal = getUserWalletBal(m);
                            return (
                              <div
                                key={m._id}
                                onClick={() => handleSelectUser(m)}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 8,
                                  border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                                  background: isSelected ? '#f0fdf4' : '#ffffff',
                                  marginBottom: 6,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  transition: 'all 0.12s ease',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.backgroundColor = '#f0f9ff';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                  }
                                }}
                              >
                                <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                      color: '#0f172a',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {m.name}
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    <span style={{ color: '#0284c7', fontWeight: 600 }}>
                                      {m.refer_id || 'No Code'}
                                    </span>{' '}
                                    • {m.mobile}
                                  </div>
                                </div>

                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                                    Bal:
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.82rem',
                                      fontWeight: 700,
                                      color: '#059669',
                                    }}
                                  >
                                    ₹{bal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                        Hover over a role on the left to see members
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Transfer Amount Input & Quick Presets */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontWeight: 700, color: '#334155', marginBottom: 6, fontSize: '0.88rem' }}>
          2. Transfer Amount (INR) <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <Input
          type="number"
          prefix="₹"
          placeholder={`Enter amount (Max ₹${poolBal.toLocaleString('en-IN')})`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          size="large"
          style={{
            borderRadius: 8,
            fontSize: '1.1rem',
            fontWeight: 700,
            borderColor: numAmount > poolBal ? '#ef4444' : undefined,
          }}
        />

        {/* Quick Amount Buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {[500, 1000, 2000, 5000, 10000].map((val) => (
            <Button
              key={val}
              size="small"
              onClick={() => handleQuickAmount(val)}
              disabled={val > poolBal}
              style={{ borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}
            >
              +₹{val.toLocaleString('en-IN')}
            </Button>
          ))}
          <Button
            size="small"
            type="dashed"
            onClick={() => handleQuickAmount(poolBal)}
            disabled={poolBal <= 0}
            style={{
              borderRadius: 6,
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#059669',
              borderColor: '#10b981',
            }}
          >
            Max Full (₹{poolBal.toLocaleString('en-IN')})
          </Button>
        </div>

        {numAmount > poolBal && (
          <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 4, fontWeight: 600 }}>
            <ExclamationCircleOutlined /> Amount cannot exceed available pool balance (₹
            {poolBal.toLocaleString('en-IN', { minimumFractionDigits: 2 })})
          </div>
        )}
      </div>

      {/* Step 3: Live Balance Projection Preview */}
      {selectedUser && numAmount > 0 && numAmount <= poolBal && (
        <div
          style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#166534',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            📊 Live Balance Effect Projection
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Reversal Pool Remaining:</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#dc2626' }}>
                ₹{poolBal.toFixed(2)} → ₹{poolNewBal.toFixed(2)} (-₹{numAmount.toFixed(2)})
              </div>
            </div>

            <ArrowRightOutlined style={{ color: '#10b981', fontSize: '1.2rem' }} />

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{selectedUser.name}'s New Balance:</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#16a34a' }}>
                ₹{userCurrentBal.toFixed(2)} → ₹{userNewBal.toFixed(2)} (+₹{numAmount.toFixed(2)})
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Remark and TPIN */}
      <div className="row g-3" style={{ marginBottom: 12 }}>
        <div className="col-md-7 col-12">
          <label style={{ display: 'block', fontWeight: 700, color: '#334155', marginBottom: 4, fontSize: '0.85rem' }}>
            Remark / Reference
          </label>
          <Input
            placeholder="Enter reason or remark..."
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            style={{ borderRadius: 6 }}
          />
        </div>

        <div className="col-md-5 col-12">
          <label style={{ display: 'block', fontWeight: 700, color: '#334155', marginBottom: 4, fontSize: '0.85rem' }}>
            Admin TPIN <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Input.Password
            prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Enter your TPIN"
            value={tpin}
            onChange={(e) => setTpin(e.target.value)}
            autoComplete="new-password"
            style={{ borderRadius: 6 }}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ReversalAssignModal;
